/**
 * CommunityMessageItem — a single community-wall message row: the optional
 * reply-preview bubble, the main text bubble (linkified), and the author-info +
 * reactions row below it.
 *
 * Flutter source of truth:
 *  - `community_message_item.dart:50-99` (`build` / `messageItem`) — column of
 *    the reply preview (when `communityReply != null`) ABOVE the main content.
 *  - `:101-114` (`messageContent`) — type switch. TEXT/GIFT →
 *    `messageTypeText`; IMAGE and VIDEO → `messageTypeMedia`
 *    (`CommunityMessageMediaCard`; VIDEO adds the play-arrow overlay). LIVE
 *    (`messageTypeLive`, `:482-558`) → the standalone `CommunityLiveMessageItem`
 *    tile (its own full row, NOT the shared reply/reactions scaffold).
 *  - `:116-174` (`messageTypeText`, main branch) — the linkified text bubble +
 *    `messageReactions` below it.
 *  - `:352-480` (`messageTypeMedia`, main branch) — the image card
 *    (`CommunityMessageMediaCard`) + the SAME `messageReactions` below it.
 *  - `:247-261` (`messageLinkText`) — linkify the message text.
 *  - `:270-286` (`messageReactions`) — author info, spacer, the reply icon
 *    (`messageReplyReaction`, `:288-302`), then the like reaction
 *    (`messageLikeReaction`, `:304-350`) at the row's end.
 *  - `:52-55` — peer vs own bubble color
 *    (`isPeer = messageUsersInfo.userId != currentUid`).
 *
 * Peer/own bubble colors map to the chat theme tokens (Flutter
 * `peerMessageColor` / `ownMessageColor`): `theme.chat.peerMessage` (peer) /
 * `theme.chat.ownMessage` (own).
 *
 * Component split (`presentation.md` § Component size): the author-info row is
 * `CommunityMessageAuthorInfo`; the reply preview is
 * `CommunityMessageReplyPreview`.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { linkifyText } from '@/presentation/utils/linkifyText';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useEnterOrSpaceActivation } from '@/presentation/hooks/useEnterOrSpaceActivation';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { CommunityMessageAuthorInfo } from './CommunityMessageAuthorInfo';
import { CommunityMessageReplyPreview } from './CommunityMessageReplyPreview';
import { CommunityMessageLikeButton } from './CommunityMessageLikeButton';
import { CommunityMessageMediaCard } from './CommunityMessageMediaCard';
import { CommunityLiveMessageItem } from './CommunityLiveMessageItem';

export interface CommunityMessageItemProps {
  /** The message to render. */
  message: CommunityChatMessageEntity;
  /** Current user's Firebase id — drives `isPeer` and the `"You"` author label. */
  currentUserId: string;
  /** Preformatted timestamp (`useDateFormatters().formatDate(createdTimestamp)`). */
  timestampLabel: string;
  /**
   * Tap the reply icon → set this message as the composer's reply target
   * (`community_message_item.dart:288-302` → the hook's `setReplyTarget`).
   */
  onReplyTap: (message: CommunityChatMessageEntity) => void;
  /**
   * Toggle the like on this message (`messageLikeReaction`,
   * `community_message_item.dart:304-350` → the hook's `likeMessage`). `liked` is
   * the NEW desired state emitted by `CommunityMessageLikeButton.onToggle`.
   */
  onLikeTap: (message: CommunityChatMessageEntity, liked: boolean) => void;
  /**
   * Open the media surface for a ready IMAGE or VIDEO message
   * (`community_screen.dart:714-717` → the page's `handleMediaTap`). Distinct
   * from `onReplyTap`: tapping the media opens the viewer/playback, NOT the
   * reply target. No-ops while uploading; video playback lands in Task 15 (the
   * page guards it until then).
   */
  onMediaTap: (message: CommunityChatMessageEntity) => void;
  /**
   * Tap the "Join Live" CTA on a live (`type = live`) message tile → the page
   * opens the join-confirm sheet + launches the audience live session
   * (`community_screen.dart:700-718` → the page's `handleLiveItemTap`).
   * Forwarded straight to `CommunityLiveMessageItem`; inert once the session
   * has ended (`community_message_item.dart:519-522`).
   */
  onLiveItemTap: (message: CommunityChatMessageEntity) => void;
}

export const CommunityMessageItem: React.FC<CommunityMessageItemProps> = ({
  message,
  currentUserId,
  timestampLabel,
  onReplyTap,
  onLikeTap,
  onMediaTap,
  onLiveItemTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // `isPeer = messageUsersInfo.userId != currentUid` (`community_message_item.dart:52-53`).
  const isPeer = message.messageUsersInfo.userId !== currentUserId;
  const bubbleColor = isPeer ? theme.chat.peerMessage : theme.chat.ownMessage;

  const reply = message.communityReply;
  const isGift = message.type === CommunityChatMessageType.gift;

  // Whole-bubble tap-to-reply (`community_screen.dart:697-699`): for a TEXT
  // message, `_onItemTap` routes straight to `_onReplyTap`, so tapping anywhere
  // on the main text bubble sets/toggles it as the reply target. Keyboard
  // activation mirrors the chat `TextBubble` (`role="button"` + this hook),
  // since the bubble hosts linkified `<a>` children and cannot be a native
  // `<button>`.
  const handleBubbleTap = (): void => onReplyTap(message);
  const handleBubbleKeyDown =
    useEnterOrSpaceActivation<HTMLDivElement>(handleBubbleTap);

  // Reply-icon reaction (`messageReplyReaction`, `:288-302`): tap/keyboard both
  // route to `onReplyTap`, sharing the one activation hook with the bubble above.
  const handleReplyTap = (): void => onReplyTap(message);
  const handleReplyKeyDown =
    useEnterOrSpaceActivation<HTMLDivElement>(handleReplyTap);

  // Flutter `messageContent` (`community_message_item.dart:105-113`) routes
  // text/gift → `messageTypeText` and image/video → `messageTypeMedia` (both
  // media types share the same card; VIDEO adds a play-arrow overlay inside it).
  // All branches share the outer scaffold below (reply preview above + reactions
  // row below), differing only in the tappable main content.
  const renderAsText =
    message.type === CommunityChatMessageType.text || isGift;
  const renderAsMedia =
    message.type === CommunityChatMessageType.image ||
    message.type === CommunityChatMessageType.video;
  if (!renderAsText && !renderAsMedia) {
    // LIVE (`messageTypeLive`, `community_message_item.dart:482-558`) is its OWN
    // standalone full-width row — it does NOT use the reply-preview / reactions
    // scaffold below, so it returns early like Flutter's separate branch.
    if (message.type === CommunityChatMessageType.live) {
      return (
        <CommunityLiveMessageItem
          message={message}
          currentUserId={currentUserId}
          timestampLabel={timestampLabel}
          onLiveItemTap={onLiveItemTap}
        />
      );
    }
    // Any genuinely-unhandled future type renders nothing.
    return null;
  }

  return (
    <div
      {...qaAttr({ id: `community-message-${message.id}` })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: responsive.spacing.xs,
        paddingTop: responsive.spacing.base,
        paddingBottom: responsive.spacing.base,
      }}
    >
      {/* Reply preview bubble, ABOVE the main bubble (`community_message_item.dart:95`). */}
      {reply !== undefined && (
        <CommunityMessageReplyPreview
          reply={reply}
          currentUserId={currentUserId}
          // Tap-target parity: the reply-preview author tap targets the MAIN
          // author (`community_message_item.dart:201`), NOT `reply.userId`.
          authorTapUserId={message.messageUsersInfo.userId}
          timestampLabel={timestampLabel}
          mainMessageIsGift={isGift}
          messageId={message.id}
        />
      )}

      {/* Main content. TEXT/GIFT → the linkified text bubble; IMAGE/VIDEO → the
          media card. Only the main content differs between the branches; the
          reply preview above and the reactions row below are the shared
          scaffold. */}
      {renderAsText ? (
        // Main text bubble (`messageTypeText`, linkified — `:147-160, 247-261`).
        // The whole bubble is tap-to-reply: Flutter's `_onItemTap` routes a TEXT
        // bubble tap to `_onReplyTap` (`community_screen.dart:697-699`). Anchor
        // clicks inside `linkifyText` already `stopPropagation`, so tapping a
        // link opens the URL instead of toggling the reply target.
        <div
          {...qaAttr({ id: `community-message-bubble-${message.id}` })}
          role="button"
          tabIndex={0}
          onClick={handleBubbleTap}
          onKeyDown={handleBubbleKeyDown}
          style={{
            width: '100%',
            backgroundColor: bubbleColor,
            borderRadius: responsive.borderRadius.lg,
            padding: responsive.spacing.sm,
            color: theme.white,
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.regular,
            cursor: 'pointer',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            boxSizing: 'border-box',
          }}
        >
          {linkifyText(message.text)}
        </div>
      ) : (
        // Main media card (`messageTypeMedia`, `:352-480`) — image or video.
        // Centered like Flutter's `Center` wrap; the card handles the uploading
        // placeholder, the video play-arrow overlay, the tap-to-open, and the
        // uploading no-op.
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <CommunityMessageMediaCard message={message} onMediaTap={onMediaTap} />
        </div>
      )}

      {/* Reactions row (`messageReactions`, `:270-286`): author info, spacer,
          the reply icon, then the like reaction at the row's end. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: responsive.spacing.sm,
          paddingTop: responsive.spacing.xs,
        }}
      >
        <CommunityMessageAuthorInfo
          authorUserId={message.messageUsersInfo.userId}
          authorUserName={message.messageUsersInfo.userName}
          currentUserId={currentUserId}
          tapUserId={message.messageUsersInfo.userId}
          timestampLabel={timestampLabel}
        />
        <div style={{ flex: 1 }} />
        {/* Reply reaction (`messageReplyReaction`, `:288-302`). */}
        <div
          {...qaAttr({ id: `community-message-reply-action-${message.id}` })}
          role="button"
          tabIndex={0}
          onClick={handleReplyTap}
          onKeyDown={handleReplyKeyDown}
          style={{ cursor: 'pointer', display: 'flex', flexShrink: 0 }}
        >
          <AssetIcon
            path="/assets/images/reply.png"
            width={responsive.icon.medium}
            height={responsive.icon.medium}
            color={theme.white}
          />
        </div>
        
        {/* Small gap */}
        <div style={{ width: responsive.spacing.sm }} />

        {/* Like reaction (`messageLikeReaction`, `:282, 304-350`) — at the row's
            end, AFTER the reply icon. `size` matches the reply icon
            (`responsive.icon.medium`) since Flutter uses the same
            `communityMessageLikeIconSize` for both. `onToggle` receives the NEW
            desired like state (`!isLiked`); the own-message no-op + in-flight
            guard live in `useCommunity.likeMessage`. */}
        <CommunityMessageLikeButton
          messageId={message.id}
          likes={message.likes}
          size={responsive.icon.medium}
          onToggle={(liked) => onLikeTap(message, liked)}
        />
      </div>
    </div>
  );
};
